import { useAuth0 } from '@auth0/auth0-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    sendUserSupportMessage,
    sendUserSupportUpdateAssigneeRequest,
    sendUserSupportUpdateStatusRequest,
    setUserSupportConversation,
    setUserSupportReferral,
} from 'services/userSupportHelpers';
import {
    UNREAD_COUNTS_STALE_SECONDS,
    useGetConversationFactsQuery,
    useGetMessagesQuery,
    useGetReferralConversationsQuery,
    useGetUnreadCountsQuery,
    useSendMessageMutation,
    useUpdateConversationAssigneeMutation,
    useUpdateConversationStatusMutation,
} from 'store/api/userSupportChatApi';
import {
    registerUser,
    updateCurrentReferral,
    updateReferralConversations,
    updateUser,
} from 'store/userSupport/userSupportSlice';

export const useUserProfile = () => {
    const { user } = useAuth0();
    const dispatch = useDispatch();
    const userProfile = useSelector((state) => state.userSupport.userProfile);
    useEffect(() => {
        if (user && !userProfile) {
            dispatch(
                registerUser({
                    details: {
                        first_name: user.given_name,
                        last_name: user.family_name,
                    },
                }),
            );
        }
    }, [dispatch, user, userProfile]);
    return {
        userProfile,
        updateUser: (details) => dispatch(updateUser({ details })),
    };
};

/**
 * Open / unread conversation counts for badges. With userSupportRestFetch on they come from the
 * unread_counts endpoint (reused for UNREAD_COUNTS_STALE_SECONDS across mounting components);
 * otherwise from the socket-fed slice.
 */
export const useUnreadCounts = ({ skip = false } = {}) => {
    const { userSupportRestFetch } = useFlags();
    const { data } = useGetUnreadCountsQuery(undefined, {
        skip: skip || !userSupportRestFetch,
        refetchOnMountOrArgChange: UNREAD_COUNTS_STALE_SECONDS,
    });
    const unreadConversationCount = useSelector(
        (state) => state.userSupport.unreadConversationCount,
    );
    const referralConversationCounts = useSelector(
        (state) => state.userSupport.referralConversationCounts,
    );
    if (userSupportRestFetch) {
        return data ?? {};
    }
    return { unreadConversationCount, referralConversationCounts };
};

export const useUserSupportMessages = () => {
    const dispatch = useDispatch();
    const { userSupportRestWrites, userSupportRestFetch } = useFlags();
    const conversationId = useSelector((state) => state.userSupport.currentConversation);
    const socketMessages = useSelector(
        (state) => state.userSupport.conversationMessages[conversationId]?.messages,
    );
    const { data: restMessages } = useGetMessagesQuery(conversationId, {
        skip: !userSupportRestFetch || !conversationId,
    });
    const { data: facts, isFetching: factsFetching } = useGetConversationFactsQuery(
        conversationId,
        { skip: !conversationId },
    );
    useEffect(() => {
        if (conversationId && !userSupportRestFetch) {
            dispatch(setUserSupportConversation(conversationId));
        }
    }, [conversationId, dispatch, userSupportRestFetch]);

    const [sendMessageMutation] = useSendMessageMutation();
    const [updateStatusMutation] = useUpdateConversationStatusMutation();
    const [updateAssigneeMutation] = useUpdateConversationAssigneeMutation();

    const sendMessageViaRest = ({ conversation_id: sendConversationId, details }) =>
        sendMessageMutation({ conversationId: sendConversationId, details }).unwrap();

    const updateStatusViaRest = ({
        conversation_id: statusConversationId,
        new_status: newStatus,
    }) => updateStatusMutation({ conversationId: statusConversationId, newStatus }).unwrap();

    const updateAssigneeViaRest = ({
        conversation_id: assigneeConversationId,
        assignee_upn: assigneeUpn,
    }) => updateAssigneeMutation({ conversationId: assigneeConversationId, assigneeUpn }).unwrap();

    const messages = socketMessages ? Object.values(socketMessages) : undefined;

    return {
        messages: userSupportRestFetch ? restMessages : messages,
        facts,
        // Facts known to be current: withheld while a refetch is in flight
        settledFacts: factsFetching ? undefined : facts,
        writesViaRest: !!userSupportRestWrites,
        fetchViaRest: !!userSupportRestFetch,
        sendMessage: userSupportRestWrites
            ? sendMessageViaRest
            : (message) => dispatch(sendUserSupportMessage(message)),
        updateConversationStatus: userSupportRestWrites
            ? updateStatusViaRest
            : (message) => dispatch(sendUserSupportUpdateStatusRequest(message)),
        updateConversationAssignee: userSupportRestWrites
            ? updateAssigneeViaRest
            : (message) => dispatch(sendUserSupportUpdateAssigneeRequest(message)),
    };
};

export const useUserSupportConversations = (referralId) => {
    const dispatch = useDispatch();
    const { userSupportRestFetch } = useFlags();
    const socketConversations = useSelector((state) => state.userSupport.referralConversations);
    const { data: restConversations } = useGetReferralConversationsQuery(referralId, {
        skip: !userSupportRestFetch || !referralId,
    });
    const referralConversationCounts = useUnreadCounts().referralConversationCounts?.[referralId];

    let conversationList;
    if (userSupportRestFetch) {
        // Per-conversation unread comes from is_unread on the REST list response
        conversationList = restConversations;
    } else {
        conversationList = socketConversations ? Object.values(socketConversations) : undefined;
        if (referralConversationCounts) {
            conversationList = conversationList?.map((conversation) => ({
                ...conversation,
                isUnread:
                    referralConversationCounts?.conversations[conversation.conversationId] ?? false,
            }));
        }
    }

    useEffect(() => {
        if (!referralId) {
            return undefined;
        }
        if (userSupportRestFetch) {
            dispatch(updateCurrentReferral({ referralId }));
            return () => dispatch(updateCurrentReferral());
        }
        dispatch(setUserSupportReferral(referralId));
        return () => {
            dispatch(updateReferralConversations({ conversations: undefined }));
            dispatch(setUserSupportReferral(null));
            dispatch(updateCurrentReferral());
        };
    }, [dispatch, referralId, userSupportRestFetch]);
    return {
        conversationList,
        referralConversationCounts,
    };
};
